import React, { useState, useLayoutEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { scheduleConnectionEmbeddingIndex } from '../../lib/connectionEmbeddings';
import { normalizeConnectionEmail } from '../../lib/connectionEmail';
import { X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';

export interface Connection {
  id: number;
  full_name: string;
  email_address: string;
  icp?: string;
  role?: string;
  linkedin_url?: string;
  last_contact_at?: string | null;
  callMeWhen1?: string | null;
  callMeWhen2?: string | null;
  callMeWhen3?: string | null;
  created_at: string;
  updated_at?: string;
  /** Vector index: none | pending | ready | error */
  connection_embeddings_status?: string | null;
  connection_embeddings_indexed_at?: string | null;
}

function isoToDatetimeLocal(iso: string | undefined | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface ConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: Connection | null;
  /** When creating a new connection, prefills the form (e.g. from Calendly import). */
  initialDraft?: Partial<Connection> | null;
  onSuccess: () => void;
}

const ConnectionModal: React.FC<ConnectionModalProps> = ({
  isOpen,
  onClose,
  connection,
  initialDraft,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Connection>({
    id: 0,
    full_name: '',
    email_address: '',
    icp: '',
    role: '',
    linkedin_url: '',
    last_contact_at: null,
    created_at: new Date().toISOString(),
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (connection) {
      setFormData(connection);
    } else {
      const d = initialDraft;
      setFormData({
        id: 0,
        full_name: d?.full_name ?? '',
        email_address: d?.email_address ?? '',
        icp: d?.icp ?? '',
        role: d?.role ?? '',
        linkedin_url: d?.linkedin_url ?? '',
        last_contact_at: d?.last_contact_at ?? null,
        created_at: new Date().toISOString(),
      });
    }
  }, [connection, isOpen, initialDraft]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLastContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) {
      setFormData(prev => ({ ...prev, last_contact_at: null }));
      return;
    }
    const iso = new Date(v).toISOString();
    setFormData(prev => ({ ...prev, last_contact_at: iso }));
  };

  const handleSave = async () => {
    const fullName = formData.full_name.trim();
    const emailAddress = normalizeConnectionEmail(formData.email_address);

    if (!fullName || !emailAddress) {
      toast.error('Full name and email address are required');
      return;
    }

    try {
      setIsSaving(true);

      if (connection) {
        const { error } = await supabase
          .from('connections')
          .update({
            full_name: fullName,
            email_address: emailAddress,
            icp: formData.icp || null,
            role: formData.role || null,
            linkedin_url: formData.linkedin_url || null,
            last_contact_at: formData.last_contact_at || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', connection.id);

        if (error) throw error;
        toast.success('Connection updated successfully');
        scheduleConnectionEmbeddingIndex(connection.id);
      } else {
        const { data: created, error } = await supabase
          .from('connections')
          .insert({
            full_name: fullName,
            email_address: emailAddress,
            icp: formData.icp || null,
            role: formData.role || null,
            linkedin_url: formData.linkedin_url || null,
            last_contact_at: formData.last_contact_at || null,
          })
          .select('id')
          .single();

        if (error) throw error;
        toast.success('Connection created successfully');
        if (created?.id != null) {
          scheduleConnectionEmbeddingIndex(created.id as number);
        }
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      reportUiError('connections', connection ? 'update' : 'create', error);
      toast.error(`Error saving connection: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!connection) return;

    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('connections')
        .delete()
        .eq('id', connection.id);

      if (error) throw error;
      toast.success('Connection deleted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      reportUiError('connections', 'delete', error, { connection_id: String(connection.id) });
      toast.error(`Error deleting connection: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[calc(100dvh-0.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-gray-800 sm: sm:rounded-lg min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <div className="min-w-0 pr-3">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
              {connection ? 'Edit Connection' : 'New Connection'}
            </h2>
            {!connection && initialDraft && (initialDraft.email_address || initialDraft.full_name) && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Prefilled from Calendly — review and save.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
          <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role
            </label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., CEO, CTO"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ICP</label>
            <textarea
              name="icp"
              value={formData.icp}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y min-h-[5rem]"
              placeholder="Ideal customer profile"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last contact
            </label>
            <input
              type="datetime-local"
              value={isoToDatetimeLocal(formData.last_contact_at)}
              onChange={handleLastContactChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              LinkedIn URL
            </label>
            <input
              type="url"
              name="linkedin_url"
              value={formData.linkedin_url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://linkedin.com/in/..."
            />
          </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div>
            {connection && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center px-3 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionModal;
